import {useI18n} from '@/i18n'
import {Strong} from '@/components/shared/Strong'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {Info} from 'lucide-react'
import {TemplateSelector} from '../components/TemplateSelector'
import {ExistingPartySelector} from '../components/ExistingPartySelector'
import type {OnboardingFormState} from '../types'
import type {Party} from '@/types/party'

interface PersonStepProps {
    form: OnboardingFormState
    onUpdate: <K extends keyof OnboardingFormState>(field: K, value: OnboardingFormState[K]) => void
    onUpdateMultiple: (fields: Partial<OnboardingFormState>) => void
    onSetTemplate: (key: string) => void
}

/**
 * Step 1: Person — template selection.
 * Supports "New party" and "Add identity to existing party" modes.
 */
export function PersonStep({form, onUpdateMultiple, onSetTemplate}: PersonStepProps) {
    const i18n = useI18n()

    const handleModeChange = (mode: 'new' | 'existing') => {
        onUpdateMultiple({
            mode,
            existingPartyId: null,
            existingPartyVersion: null,
            existingPartyData: null,
        })
    }

    const handleExistingPartySelect = (party: Party | null) => {
        if (!party) {
            onUpdateMultiple({
                existingPartyId: null,
                existingPartyVersion: null,
                displayName: '',
                existingPartyData: null,
            })
            return
        }
        onUpdateMultiple({
            existingPartyId: String(party.id),
            existingPartyVersion: party.version,
            displayName: party.derivedDisplayName,
            partyType: party.partyType,
        })
    }

    return (
        <Stack gap="lg">
            {/* Template switch info banner */}
            {form.templateSwitched && (
                <InfoBanner variant="info" icon={Info}>
                    {i18n.onboarding.person.templateSwitchInfo}
                </InfoBanner>
            )}

            {/* Mode toggle: New / Existing */}
            <InlineRow gap="lg">
                <InlineRow gap="xs">
                    <input
                        type="radio"
                        name="person-mode"
                        checked={form.mode === 'new'}
                        onChange={() => handleModeChange('new')}
                        className={`accent-primary`}
                    />
                    <Strong>{i18n.onboarding.mode.newPerson}</Strong>
                </InlineRow>
                <InlineRow gap="xs">
                    <input
                        type="radio"
                        name="person-mode"
                        checked={form.mode === 'existing'}
                        onChange={() => handleModeChange('existing')}
                        className={`accent-primary`}
                    />
                    <Strong>{i18n.onboarding.mode.existingPerson}</Strong>
                </InlineRow>
            </InlineRow>

            {form.mode === 'new' && (
                <TemplateSelector
                    selectedKey={form.templateKey}
                    onSelect={onSetTemplate}
                />
            )}

            {form.mode === 'existing' && (
                <ExistingPartySelector
                    selectedPartyId={form.existingPartyId}
                    onSelect={handleExistingPartySelect}
                />
            )}
        </Stack>
    )
}
